/**
 * PAYING ON STRIPE'S PAGE, AND COMING BACK: what her browser remembers between pressing Pay on an
 * invoice and landing back on `/payments/return`, how that page's address is read, and the one line
 * that sends her to Stripe's page. Ops makes the page and decides what was paid (CLAUDE.md binding
 * rule 6 and rule 1); this file only keeps the screens from offering again what she has just done.
 *
 * - THE ADDRESS BACK (the seam, l.677-687 at b727ec1) is one page on home's origin with nothing of
 *   hers in it. Ops adds `page` (`bank`, `card` or `all`) and, where the page could save a method,
 *   `saves` (`account`, her tick on the bank page, or `box`, Stripe's box that saves a card) after a
 *   payment, and `not-paid` after Stripe's back arrow.
 *   `not-paid` is read by its presence, since the contract does not say how it is written, and
 *   before anything else, so an address carrying both never says a payment was made. Any other value
 *   is ignored, and an address with no payment to read says nothing was paid.
 * - SIGNED OUT, THE SAME WORDS (ops's plan for clients reaching only home, section 7, ask 3): the
 *   office's page, and a page her texted link made, come back to the invoice's own page rather than
 *   to `/payments/return`, so the door drawn at either with no session reads the address back there
 *   (`returnAt`) and says it above the sign-in (`screens/Door.tsx`). Signed in, the invoice's own
 *   page says it at its top (`readReturn`, `screens/Invoice.tsx`, P110), and after a payment notes
 *   it for that invoice (`noteReturnTo`): as the return page does, where this tab left to pay that
 *   very invoice, and otherwise as P35's mark, so its ways to pay are held as on its way whichever
 *   tab she paid from; after `not-paid` it notes nothing. It notes a return once for the page's
 *   entry in the history, which keeps that it did, so a reload, Back or a restored tab opening that
 *   entry again after ops's push let the mark go marks nothing anew (the review of 3efb79f).
 * - THE TAB REMEMBERS THE INVOICE, NEVER THE PAGE. Just before she leaves for Stripe's page, the
 *   invoice's id is kept in `sessionStorage`, which is this tab's alone and goes with it; the page's
 *   address is never kept. So only the tab she left from reads the return as hers, and only its
 *   return page, after Stripe's back arrow, leads back to that invoice, once, as it forgets it.
 *   With it the tab keeps the way she pressed Pay on, the bank or the card (slice V3, after the walk
 *   was replayed, whose way back opened on Zelle), so the way back opens the invoice on that way.
 * - EVERY TAB OF HER BROWSER HOLDS THE MARK (P35). Coming back paid marks that invoice on its way in
 *   `localStorage`, with the time she came back, so Payments and the invoice's page, in this tab
 *   and every other tab of her browser, hold back its Pay and say why, until ops's push says how it
 *   stands. The owner's walk of 2026-10-16 met another tab still offering "Pay $225" after she had
 *   paid, where Zelle asks ops nothing and could be paid twice. The mark is forgotten, in every
 *   tab, when a read shows nothing to pay on it from here (`forget`), so a push that later offers
 *   it again is offered; a day after she came back, by when ops's push, normally two minutes, has
 *   long come; and when she signs out (`forgetAll`). Stripe's back arrow says only that this time
 *   nothing was paid, so it leaves a payment already on its way as it is. At most the last twenty
 *   are kept.
 * - SENT BY ZELLE IS THE WORKER'S, AND OPS'S (round four, slice M1; round five, slice B2, ops's
 *   section 11.7, P53). Slice V2 had "I sent it by Zelle" write this very mark, in this browser
 *   alone, for a day: a Zelle sent on a Saturday came back as owed with Pay on the Sunday, before the
 *   office (closed on Sundays) could match it, and her other devices never stopped offering Pay. Her
 *   report goes to ops now, the Worker holds the invoice on its way from her press until ops's
 *   answer governs, and carries it on each read; this file writes no mark for it. A mark V2 left for
 *   Zelle has the shape of any mark, so it is read as one, a day at most, and forgotten as one:
 *   harmless. When the request door takes her report, the tab that asked tells every other tab of
 *   her browser (`tellZelleWord`), with a value of its own under a key of its own and nothing of
 *   hers, so each reads its place again at once; the tab that asked reads its own again itself. Her
 *   other devices draw it on their next read.
 * - EVERY OPEN TAB HEARS A CHANGE (slice V2): a place draws from its own view of the marks
 *   (`marksKept`, `onItsWayIn`), and listens (`onMarksChanged`) for a write made in this tab, once
 *   it is done, and for one made in another tab, as the browser's `storage` event says it, so a tab
 *   already open when she pays draws the mark at once, without a reload
 *   (`components/Guarded.tsx`); and for another tab's word that it sent an invoice by Zelle, as a
 *   change made elsewhere that holds nothing anew, so the place reads itself again.
 * - THE WORDS BESIDE HER TICK, CHANGED (ops's section 12.3, round five, slice A4): across the load
 *   afresh that draws the current words after ops's `409 terms_changed`, the tab keeps the invoice
 *   and the way she pressed on, in its own `sessionStorage`, and the page forgets it once said
 *   (`noteTermsChanged`).
 * - BACK FROM A PAGE THAT SAVES A WAY TO PAY (ops's section 12.2, round five, slice A5): How you pay
 *   is that page's way back, and reads `saved=setup` beside `page`, or `not-saved`
 *   (`readSetupReturn`); the page after paying reads neither as a payment. Across a load afresh after
 *   ops's `409 terms_changed` the tab keeps the kind she pressed on (`noteSetupTermsChanged`).
 * - NOTHING HERE SAYS PAID. An invoice reads paid only when ops pushes `paid` (the seam, l.687).
 * - A BROWSER THAT KEEPS NOTHING, or keeps what this file did not write, keeps nothing: every read
 *   and write is guarded, a mark is held only in the shape this file writes and only for a day
 *   before now by this browser's clock, and the screens then offer what they would without it.
 * - A LINK THAT NAMES A WAY TO PAY (`?pay=`, P107) is read here (`wayAsked`), and the invoice's page
 *   opens on that way where it is offered, as Pay lands on the ways to pay.
 */
import { matchPath } from 'react-router-dom';
import { INVOICE_ROUTE, PAY_QUERY, PAYMENT_RETURN_PATH } from '../paths.ts';

/** Ops's words for the page she paid on. `all` is the office's page, which takes either. */
export type PaidOn = 'bank' | 'card' | 'all';

/** Ops's words for what the page saved: her tick on home's bank screen, or the box on Stripe's. */
export type Saves = 'account' | 'box';

/** What the address back says: a payment made, on which page and saving what, or none made. */
export type Back =
  { readonly paid: true; readonly page: PaidOn; readonly saves?: Saves } | { readonly paid: false };

const PAGES: readonly PaidOn[] = ['bank', 'card', 'all'];
const SAVES: readonly Saves[] = ['account', 'box'];

/**
 * The invoice she left this tab to pay on Stripe's page, in the tab's own `sessionStorage`, under
 * this name, which the browser suite also writes to stand in for a press of Pay.
 */
export const LEAVING_FOR = 'home.paying.leftFor';

/**
 * The way to pay she pressed Pay on as she left this tab for Stripe's page, `bank` or `card`, in the
 * tab's own `sessionStorage` beside the invoice, under this name; none where the press noted none.
 */
export const LEFT_FROM = 'home.paying.leftFrom';

/** The ways to pay that leave for Stripe's page: the bank's and the card's. */
export type LeftFrom = 'bank' | 'card';

const LEFT_FROMS: readonly LeftFrom[] = ['bank', 'card'];

/** The invoice she left this tab to pay, and the way she left it from, where the tab noted one. */
export interface Left {
  readonly invoiceId: string;
  readonly way?: LeftFrom;
}

/**
 * What a link hands an invoice's page in the browser's history, never in its address, as the ways
 * to pay are held in the page and never in its address: `pay`, that she pressed Pay on Payments, so
 * the page brings its ways to pay into view as she arrives (`cameToPay`, slice M2 of round four);
 * and `way`, the way she left it from for Stripe's page, to open it on (`opensOn`), which the Pay
 * on the page Stripe's back arrow returns to hands it.
 */
export interface OpenOn {
  readonly pay?: true;
  readonly way?: LeftFrom;
}

/** The way an invoice's page opens on, from what the history holds for it, or none. */
export function opensOn(state: unknown): LeftFrom | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const { way } = state as { readonly way?: unknown };
  return LEFT_FROMS.find((each) => each === way);
}

/** Whether she came to an invoice's page by pressing Pay, as the history holds it for the page. */
export function cameToPay(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) return false;
  return (state as { readonly pay?: unknown }).pay === true;
}

/** The ways to pay a link may name: Zelle's screen, the bank's and the card's. */
export type WayToPay = 'zelle' | LeftFrom;

const WAYS_TO_PAY: readonly WayToPay[] = ['zelle', ...LEFT_FROMS];

/**
 * The way to pay a link ops put in front of her names in the address of an invoice's page (`?pay=`,
 * the owner's decision of 2026-10-19, ops #76, `docs/seam.md` P107), or none for anything else. The
 * page opens on it only where that way is offered for that invoice (`screens/Invoice.tsx`).
 */
export function wayAsked(search: string): WayToPay | undefined {
  const named = new URLSearchParams(search).get(PAY_QUERY);
  return WAYS_TO_PAY.find((each) => each === named);
}

/**
 * The invoices she came back from paying, each with when, oldest first, in `localStorage`, which
 * every tab of her browser shares. An earlier build kept a list of ids under this name in each
 * tab's own storage, which is never read now.
 */
const ON_ITS_WAY = 'home.paying.onItsWay';

/** How many invoices on their way her browser keeps. */
const KEPT_AT_MOST = 20;

/** How long a mark holds at most, from when she came back: a day. */
const HELD_FOR_MS = 24 * 60 * 60 * 1000;

/** An invoice on its way, as this file writes it: its id, and when she came back, in ms. */
interface Mark {
  readonly id: string;
  readonly at: number;
}

/**
 * Reads the address back, `location.search`; null when it says nothing this build can read. An
 * address a setup page comes back with (`saved` or `not-saved`, ops's section 12.2) says nothing of a
 * payment, since none was made on it, whatever else it carries (`readSetupReturn`).
 */
export function readReturn(search: string): Back | null {
  const query = new URLSearchParams(search);
  if (query.has(SAVED) || query.has(NOT_SAVED)) return null;
  if (query.has('not-paid')) return { paid: false };
  const page = PAGES.find((each) => each === query.get('page'));
  if (page === undefined) return null;
  const saves = SAVES.find((each) => each === query.get('saves'));
  return saves === undefined ? { paid: true, page } : { paid: true, page, saves };
}

/**
 * The addresses a Stripe page returns to on home: an invoice's own page, where the office's page and
 * a page a texted link made come back (ops's plan for clients reaching only home, section 2), and
 * the page home's own pages come back to (`return_url`).
 */
const RETURNED_TO: readonly string[] = [INVOICE_ROUTE, PAYMENT_RETURN_PATH];

/**
 * What the address back says where a Stripe page returned her (`RETURNED_TO`), for the door drawn
 * there with no session, which says the words back above the sign-in (ops's plan for clients
 * reaching only home, section 7, ask 3; `screens/Door.tsx`); null anywhere else, and where the
 * address says nothing the contract defines (`readReturn`). It reads only the address, so it marks
 * nothing and forgets nothing.
 */
export function returnAt(location: {
  readonly pathname: string;
  readonly search: string;
}): Back | null {
  const returned = RETURNED_TO.some(
    (route) => matchPath({ path: route, end: true }, location.pathname) !== null,
  );
  return returned ? readReturn(location.search) : null;
}

/**
 * BACK FROM A PAGE THAT SAVES A WAY TO PAY WITH NOTHING OWED (ops's section 12.2, round five, slice
 * A5). Ops adds `page=<kind>` and `saved=setup` to the address How you pay gave it, or `not-saved`
 * after Stripe's back arrow, which is read by its presence and before anything else, as `not-paid`
 * is. Nothing was paid on the page, and nothing here says anything was.
 */
export type SetupBack =
  { readonly saved: true; readonly page: SetupKind } | { readonly saved: false };

/** The two kinds of page that save a way to pay. */
export type SetupKind = 'bank' | 'card';

const SETUP_KINDS: readonly SetupKind[] = ['bank', 'card'];

/** The query words of a setup page's way back, as ops adds them (12.2). */
const SAVED = 'saved';
const SETUP = 'setup';
const NOT_SAVED = 'not-saved';

/** Reads a setup page's address back; null where it says nothing this build can read. */
export function readSetupReturn(search: string): SetupBack | null {
  const query = new URLSearchParams(search);
  if (query.has(NOT_SAVED)) return { saved: false };
  if (query.get(SAVED) !== SETUP) return null;
  const page = SETUP_KINDS.find((each) => each === query.get('page'));
  return page === undefined ? null : { saved: true, page };
}

/** Where a thing is kept: in this tab's own storage, or in the one every tab of her browser shares. */
type Where = 'tab' | 'browser';

/** The storage for `where`, or null where the browser gives none. */
function storage(where: Where): Storage | null {
  try {
    return where === 'tab' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function read(where: Where, key: string): string | null {
  try {
    return storage(where)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(where: Where, key: string, value: string | null): void {
  try {
    const kept = storage(where);
    if (value === null) kept?.removeItem(key);
    else kept?.setItem(key, value);
  } catch {
    // Kept nothing: the screens offer what they would without it.
  }
}

/** A mark as this file writes it, or null for anything else kept in its place. */
function markOf(entry: unknown): Mark | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const { id, at } = entry as { readonly id?: unknown; readonly at?: unknown };
  if (typeof id !== 'string' || id === '') return null;
  return typeof at === 'number' && Number.isSafeInteger(at) ? { id, at } : null;
}

/**
 * Whether a mark still holds at `now`: she came back less than a day before, by this browser's
 * clock. A time still to come was not written by this clock as it reads now, so it holds nothing,
 * and a clock that was wrong when she came back never holds a Pay back for longer than a day.
 */
const holds = (mark: Mark, now: number): boolean => mark.at <= now && now - mark.at < HELD_FOR_MS;

/**
 * The invoices on their way that a kept value holds at `now`, oldest first, and none for what this
 * file did not write.
 */
function marksIn(value: string | null, now: number): Mark[] {
  try {
    const kept: unknown = JSON.parse(value ?? '[]');
    if (!Array.isArray(kept)) return [];
    return kept.flatMap((entry) => {
      const mark = markOf(entry);
      return mark !== null && holds(mark, now) ? [mark] : [];
    });
  } catch {
    return [];
  }
}

/** The invoices on their way that hold at `now`, oldest first, and none for what it did not write. */
const marks = (now: number): Mark[] => marksIn(marksKept(), now);

/**
 * A change to the marks her browser keeps, as a place hears it (`onMarksChanged`).
 */
export interface MarksChanged {
  /**
   * Made in another tab of her browser, which may have read what this tab's place has not: ops's
   * push that the invoice is paid, which is why that tab forgot its mark.
   */
  readonly elsewhere: boolean;
  /** It holds an invoice on its way that was not held before, so a Pay is to be held back. */
  readonly marked: boolean;
}

type Listener = (change: MarksChanged) => void;

/** The places in this tab listening for a change to the marks. */
const listeners = new Set<Listener>();

/** What changed between two kept values, by this browser's clock now. */
function changeOf(before: string | null, after: string | null, elsewhere: boolean): MarksChanged {
  const now = Date.now();
  const held = new Set(marksIn(before, now).map(({ id }) => id));
  return { elsewhere, marked: marksIn(after, now).some(({ id }) => !held.has(id)) };
}

/**
 * Tells every place in this tab of a change made here, once the write is done: after the task that
 * made it, so a write made while a screen is drawn, as the return page's, never redraws another
 * component in the middle of drawing. The browser tells every other tab itself.
 */
function tell(change: MarksChanged): void {
  queueMicrotask(() => {
    for (const listener of [...listeners]) listener(change);
  });
}

/**
 * Writes the marks' value, and tells this tab's places where that changed what is kept: a write the
 * browser refused changed nothing, and says nothing.
 */
function writeMarks(value: string | null): void {
  const before = read('browser', ON_ITS_WAY);
  if (value === before) return;
  write('browser', ON_ITS_WAY, value);
  const after = read('browser', ON_ITS_WAY);
  if (after !== before) tell(changeOf(before, after, false));
}

/**
 * Keeps these marks, the last twenty at most, and nothing once none is left, so what no longer holds
 * and what this file did not write go with any change. It writes only where that changes what is
 * kept, so a read that forgets nothing writes nothing, and tells nobody.
 */
function keep(kept: readonly Mark[]): void {
  const last = kept.slice(-KEPT_AT_MOST).map(({ id, at }) => ({ id, at }));
  writeMarks(last.length > 0 ? JSON.stringify(last) : null);
}

/** Marks an invoice on its way from now, in every tab, as the only mark it has. */
function mark(invoiceId: string): void {
  const now = Date.now();
  keep([...marks(now).filter((each) => each.id !== invoiceId), { id: invoiceId, at: now }]);
}

/**
 * Notes the invoice she is leaving this tab to pay on Stripe's page, just before she goes, and the
 * way she pressed Pay on, where given; a press that gives none leaves no way an earlier press noted.
 */
export function noteLeaving(invoiceId: string, way?: LeftFrom): void {
  write('tab', LEAVING_FOR, invoiceId);
  write('tab', LEFT_FROM, way ?? null);
}

/**
 * Notes what the address back says: paid marks the invoice she left this tab to pay on its way, in
 * every tab, from now; unpaid and nothing to read mark nothing, and an unpaid return leaves a
 * payment already on its way, as one she came back from in another tab, as it is. Either way the
 * invoice she left for is then forgotten, so reading the same return twice, as a reload does, is
 * reading it once, and a later return names only an invoice she left for after it.
 *
 * Gives back the invoice she left this tab for, with the way she left it from where the tab noted
 * one, once, as both are forgotten, so the return page can lead back to it after Stripe's back
 * arrow, on that way; null where the address says nothing it can read, which forgets nothing, and
 * where this tab noted no invoice, as a tab that never left to pay, or a reload of the page that
 * already read it. A way this file did not write is no way.
 */
export function noteReturn(back: Back | null): Left | null {
  if (back === null) return null;
  const invoiceId = read('tab', LEAVING_FOR);
  const way = LEFT_FROMS.find((each) => each === read('tab', LEFT_FROM));
  write('tab', LEFT_FROM, null);
  if (invoiceId === null) return null;
  if (back.paid) mark(invoiceId);
  write('tab', LEAVING_FOR, null);
  return way === undefined ? { invoiceId } : { invoiceId, way };
}

/**
 * What the history holds for an invoice's page once the page noted its address back
 * (`noteReturnTo`): what the entry held before, and `noted`.
 */
export type Noted = Readonly<Record<string, unknown>> & { readonly noted: true };

/** Whether the history's entry for an invoice's page holds that its address back was noted. */
function notedIn(entry: unknown): boolean {
  return (
    typeof entry === 'object' && entry !== null && (entry as { noted?: unknown }).noted === true
  );
}

/**
 * Notes the address back on an invoice's own page, signed in, for that invoice alone
 * (`screens/Invoice.tsx`, P110), so the invoice is on its way in every tab and she is not offered
 * Pay on it again before ops's push says how it stands, or the mark's day is up; never paid.
 * `entry` is what the history holds for the page (`useLocation().state`).
 *
 * - After a payment, where this tab left to pay that very invoice, it is the same `noteReturn` the
 *   return page makes, which forgets what the tab noted as it left.
 * - After a payment, where this tab noted no invoice, or another (the office's page, a page her
 *   texted link made, or a page opened from another tab, which come back here), it marks this
 *   invoice exactly as P35's mark is made, in every tab, from now (the fifth client-journey walk,
 *   2026-10-20, which found "Thank you. Your payment is on its way" over the same invoice still
 *   offering Zelle and "Pay another way"). A tab that left to pay another invoice keeps it for that
 *   invoice's own return. A mark that already holds keeps the time she came back.
 * - ONCE FOR ITS ENTRY IN THE HISTORY (the review of 3efb79f). The address back stays on the page,
 *   so a reload, Back or Forward to it, or a tab her browser restores days on opens the same entry
 *   again. Noting it again once ops's push had let the mark go (`forget`), as when her bank payment
 *   cleared and then failed, marked the invoice ops offered again as on its way for another day,
 *   in every tab. So after a payment it gives back what the entry is to hold from now on, what it
 *   held with `noted` (`Noted`), for the page to keep in the history, and for an entry that holds
 *   `noted` it notes nothing: a return is noted once for its entry. Stripe's next return is a new
 *   entry, which holds nothing yet, and is noted.
 * - After `not-paid`, and for an address it cannot read, it notes and forgets nothing.
 *
 * Gives back null where it noted nothing, so the entry is left as it is.
 */
export function noteReturnTo(invoiceId: string, back: Back | null, entry: unknown): Noted | null {
  if (back?.paid !== true || notedIn(entry)) return null;
  if (read('tab', LEAVING_FOR) === invoiceId) noteReturn(back);
  else if (!onItsWay(invoiceId)) mark(invoiceId);
  const held = typeof entry === 'object' && entry !== null ? entry : {};
  return { ...held, noted: true };
}

/**
 * THE WORDS BESIDE HER TICK HAVE CHANGED (ops's section 12.3, round five, slice A4). Ops refused a
 * ticked press `409 terms_changed`: the paragraph this tab drew beside the tick is not the one that
 * holds, and the only way to draw the one that does is the page as the Worker serves it now. So the
 * tab keeps the invoice and the way she pressed on, here, in its own `sessionStorage`, and loads the
 * page afresh (`away.again`); the invoice's page opens on that way, says so above the tick, and
 * forgets it. Nothing of hers: the invoice's id and `bank` or `card`.
 */
export const TERMS_CHANGED_ON = 'home.paying.termsChanged';

/** Notes, across the load afresh, that the words beside her tick on this invoice's way changed. */
export function noteTermsChanged(invoiceId: string, way: LeftFrom): void {
  write('tab', TERMS_CHANGED_ON, JSON.stringify({ invoiceId, way }));
}

/**
 * The way she pressed on as ops said the words beside her tick had changed, for this invoice alone,
 * as this file wrote it; undefined for another invoice, for none, and for anything it did not write.
 * Reading it forgets nothing (`forgetTermsChanged`).
 */
export function termsChangedOn(invoiceId: string): LeftFrom | undefined {
  const kept = read('tab', TERMS_CHANGED_ON);
  if (kept === null) return undefined;
  try {
    const value: unknown = JSON.parse(kept);
    if (typeof value !== 'object' || value === null) return undefined;
    const { invoiceId: id, way } = value as {
      readonly invoiceId?: unknown;
      readonly way?: unknown;
    };
    return id === invoiceId ? LEFT_FROMS.find((each) => each === way) : undefined;
  } catch {
    return undefined;
  }
}

/** Forgets the note once the page has said it. */
export function forgetTermsChanged(): void {
  write('tab', TERMS_CHANGED_ON, null);
}

/**
 * THE WORDS BESIDE A BUTTON THAT SAVES A WAY TO PAY HAVE CHANGED (ops's sections 12.2 and 12.5,
 * round five, slice A5): ops refused a press `409 terms_changed`, and How you pay is loaded afresh
 * to draw the words the Worker serves now. Across that load the tab keeps the kind she pressed on,
 * `bank` or `card`, here, in its own `sessionStorage`, and nothing of hers; the page says so above
 * that kind's words and forgets it.
 */
export const SETUP_TERMS_CHANGED = 'home.paying.setupTermsChanged';

/** Notes, across the load afresh, that the words beside this kind's button changed. */
export function noteSetupTermsChanged(kind: SetupKind): void {
  write('tab', SETUP_TERMS_CHANGED, kind);
}

/** The kind she pressed on as ops said its words had changed, as this file wrote it, or none. */
export function setupTermsChangedOn(): SetupKind | undefined {
  const kept = read('tab', SETUP_TERMS_CHANGED);
  return SETUP_KINDS.find((each) => each === kept);
}

/** Forgets the note once How you pay has said it. */
export function forgetSetupTermsChanged(): void {
  write('tab', SETUP_TERMS_CHANGED, null);
}

/**
 * Where a tab tells every other tab of her browser that the request door took her report that an
 * invoice was sent by Zelle (P53), in the storage every tab shares. Its value is new each time and
 * means nothing: no invoice, no household and nothing of hers. Its key is round four's, so a tab
 * still drawn by the build before hears it too.
 */
const ZELLE_WORD = 'home.paying.zelleWord';

/**
 * Tells every other tab of her browser, by the browser's `storage` event, that the request door took
 * her report that she sent an invoice by Zelle, so each place open there reads itself again
 * (`onMarksChanged`, `components/Guarded.tsx`). This tab's own listeners hear nothing of it: the
 * page that asked reads its place again itself. A browser that keeps nothing tells nobody.
 */
export function tellZelleWord(): void {
  write('browser', ZELLE_WORD, `${Date.now()}.${Math.random().toString(36).slice(2)}`);
}

/**
 * What her browser keeps of the payments on their way now, as it keeps it, for a place to draw from
 * (`onItsWayIn`): its view of them, which it takes afresh when it hears a change it may draw at
 * once, and with each read of its own (`components/Guarded.tsx`).
 */
export function marksKept(): string | null {
  return read('browser', ON_ITS_WAY);
}

/**
 * Whether this invoice is on its way in what was kept, `kept` (`marksKept`): she came back from
 * paying it on Stripe's page in any tab, or a mark slice V2 left for Zelle stands, within a day of
 * now.
 */
export function onItsWayIn(kept: string | null, invoiceId: string): boolean {
  return marksIn(kept, Date.now()).some((each) => each.id === invoiceId);
}

/** Whether this invoice is on its way in what her browser keeps now. */
export function onItsWay(invoiceId: string): boolean {
  return onItsWayIn(marksKept(), invoiceId);
}

/**
 * Forgets an invoice on its way, in every tab, once a read shows ops's push has said how it stands:
 * nothing to pay on it from here, as a bank payment clearing, paid, nothing owed or a saved method
 * paying it each read. A later push that offers it again, as after a bank payment that failed, is
 * then offered.
 */
export function forget(invoiceId: string): void {
  keep(marks(Date.now()).filter((mark) => mark.id !== invoiceId));
}

/**
 * Forgets everything this file kept, every payment on its way, the invoice this tab left for, with
 * the way she left it from, a note that the words beside her tick or beside a button that saves a way
 * to pay changed, and what told her tabs of a word sent by Zelle, when she signs out, so
 * nothing of hers is held for whoever signs in next on this browser.
 */
export function forgetAll(): void {
  writeMarks(null);
  write('browser', ZELLE_WORD, null);
  write('tab', LEAVING_FOR, null);
  write('tab', LEFT_FROM, null);
  write('tab', TERMS_CHANGED_ON, null);
  write('tab', SETUP_TERMS_CHANGED, null);
}

/**
 * Listens for a change to the marks, made in this tab (`tell`) or in another tab of her browser, as
 * the browser's `storage` event says it of this file's key in the storage every tab shares, or of
 * that storage emptied. Gives back the way to stop listening. A place (`components/Guarded.tsx`)
 * listens, so every open tab draws a change at once: the replay of the owner's walk of 2026-10-16
 * found a tab already open when she paid still offering Zelle and Pay. Another tab telling of a word
 * that it sent an invoice by Zelle (`tellZelleWord`, P53) is heard as a change made elsewhere that
 * holds nothing anew, so the place reads itself again and draws what the Worker says.
 */
export function onMarksChanged(listener: Listener): () => void {
  const heard = (event: StorageEvent) => {
    if (event.storageArea === null || event.storageArea !== storage('browser')) return;
    if (event.key === ZELLE_WORD) {
      listener({ elsewhere: true, marked: false });
      return;
    }
    if (event.key !== null && event.key !== ON_ITS_WAY) return;
    listener(changeOf(event.oldValue, event.newValue, true));
  };
  listeners.add(listener);
  window.addEventListener('storage', heard);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', heard);
  };
}

/**
 * Where the tab goes, in one place so a test stands in for it: to Stripe's page, which the browser
 * leaves this page for, and comes back from to `/payments/return`; and this page again, loaded
 * afresh, so it draws the words the Worker serves now (`noteTermsChanged`).
 */
export const away = {
  to(url: string): void {
    window.location.assign(url);
  },
  again(): void {
    window.location.reload();
  },
};
